import React from 'react';
import { inject, observer } from 'mobx-react';
import { action, makeObservable, observable } from 'mobx';

import { AppState } from '@store/App/App';
import { ButtonPage } from './ButtonPage';
import { ButtonModifiers, ButtonTheme } from '@components/Button';
import { OptionType } from '@components/Select';
import { OptionParams } from '@components/CheckboxList/CheckboxList';
import { CommonTestAttributes } from 'chaika-test-attributes';

const themeOptions: OptionType[] = [
    {
        value: ButtonTheme.Danger,
        label: 'Danger',
    },
    {
        value: ButtonTheme.Gradient,
        label: 'Gradient',
    },
    {
        value: ButtonTheme.Light,
        label: 'Light',
    },
    {
        value: ButtonTheme.Light,
        label: 'LightRedesign',
    },
    {
        value: ButtonTheme.Link,
        label: 'Link',
    },
    {
        value: ButtonTheme.Link,
        label: 'LinkRedesign',
    },
    {
        value: ButtonTheme.Main,
        label: 'Main',
    },
    {
        value: ButtonTheme.Primary,
        label: 'Primary',
    },
    {
        value: ButtonTheme.Secondary,
        label: 'Secondary',
    },
    {
        value: ButtonTheme.SecondaryLightHover,
        label: 'SecondaryLightHover',
    },
    {
        value: ButtonTheme.White,
        label: 'White',
    },
];

const modifiersOptions: OptionParams<ButtonModifiers>[] = [
    {
        dataTest: CommonTestAttributes.Unknown,
        value: ButtonModifiers.Big,
        label: 'Big',
    },
    {
        dataTest: CommonTestAttributes.Unknown,
        value: ButtonModifiers.CustomHeight,
        label: 'CustomHeight',
    },
    {
        dataTest: CommonTestAttributes.Unknown,
        value: ButtonModifiers.InheritContentPosition,
        label: 'InheritContentPosition',
    },
    {
        dataTest: CommonTestAttributes.Unknown,
        value: ButtonModifiers.InheritWidth,
        label: 'InheritWidth',
    },
    {
        dataTest: CommonTestAttributes.Unknown,
        value: ButtonModifiers.MobilePaddings,
        label: 'MobilePaddings',
    },
    {
        dataTest: CommonTestAttributes.Unknown,
        value: ButtonModifiers.Small,
        label: 'Small',
    },
    {
        dataTest: CommonTestAttributes.Unknown,
        value: ButtonModifiers.WithBorder,
        label: 'WithBorder',
    },
    {
        dataTest: CommonTestAttributes.Unknown,
        value: ButtonModifiers.WithoutBackground,
        label: 'WithoutBackground',
    },
    {
        dataTest: CommonTestAttributes.Unknown,
        value: ButtonModifiers.WithoutLeftAndRightPaddings,
        label: 'WithoutLeftAndRightPaddings',
    },
    {
        dataTest: CommonTestAttributes.Unknown,
        value: ButtonModifiers.WithoutPaddings,
        label: 'WithoutPaddings',
    },
];

interface StoreProps {
    app: AppState;
}

const injectableStores: (keyof StoreProps)[] = [AppState.Name];

@observer
class Component extends React.Component<StoreProps> {
    @observable public redesign: boolean = true;
    @observable public theme: OptionType = themeOptions[7];
    @observable public modifiers: ButtonModifiers[] = [];
    @observable public label: string = 'Button';
    @observable public withAddIcon: boolean = false;

    constructor(props: StoreProps) {
        super(props);
        makeObservable(this);
    }

    public async componentDidMount(): Promise<void> {
        const { app } = this.props;
        app.greyBackground = true;
    }

    public render(): React.ReactNode {
        return React.createElement(ButtonPage, {
            themeOptions,
            modifiersOptions,
            redesign: this.redesign,
            theme: this.theme,
            modifiers: this.modifiers,
            label: this.label,
            withAddIcon: this.withAddIcon,
            onRedesignChange: this.handleRedesingChange,
            onWithAddIconChangeChange: this.handleWithAddIconChangeChange,
            onLabelChange: this.handleLabelChange,
            onThemeChange: this.handleThemeChange,
            onModifiersChange: this.handleModifiersChange,
        });
    }

    @action.bound
    private handleRedesingChange() {
        this.redesign = !this.redesign;
    }

    @action.bound
    private handleWithAddIconChangeChange() {
        this.withAddIcon = !this.withAddIcon;
    }

    @action.bound
    private handleLabelChange({ target: { value } }: React.ChangeEvent<HTMLInputElement>) {
        this.label = value;
    }

    @action.bound
    private handleThemeChange(params: OptionType, name?: string | undefined) {
        this.theme = params;
    }

    @action.bound
    private handleModifiersChange(selectedValues: ButtonModifiers[]) {
        this.modifiers = selectedValues;
    }
}

export const ButtonPageContainer = inject<{}, StoreProps>(...injectableStores)(Component);
